import { useState, useEffect, useRef } from 'react';
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { Check, CheckCircle, ChevronLeft, ChevronRight, CircleCheck, Copy, XCircle, Mic, Square, Play, Pause, Info } from "lucide-react";
import server from '../../../api/server.js';
import { AiOutlineAudio } from 'react-icons/ai';
import { FaCheckCircle, FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { IoIosCloseCircle, IoMdCloseCircle } from "react-icons/io";
import { IoClose } from 'react-icons/io5';
import CallDetails from '../calls/CallDetails';
import "../../../dashboard-components/loader.css"
import { useToast } from '../../../context/ToastContext.jsx';

const AgentTraining = ({trainingData, clientID, serviceID, emailID, training_session_id, prop_back_api_call, savedState, isRefreshing, onStateRestore, training_material}) => {
    const { showToast } = useToast();
    const [modules, setModules] = useState([]);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [submittedAnswers, setSubmittedAnswers] = useState({});
    const [trainingScores, setTrainingScores] = useState({});
    const [expandedExamples, setExpandedExamples] = useState({});
    
    // New state for conversation details and audio
    const [conversationDetails, setConversationDetails] = useState({});
    const [audioLoading, setAudioLoading] = useState({});
    const [email, setEmail] = useState(null);
    const [client_id, setClient_id] = useState(null);
    const [service_id, setService_id] = useState(null);
    const audioRefs = useRef({});
    const segmentRefs = useRef({});
    const [selectedSegmentIdx, setSelectedSegmentIdx] = useState({});
    const [showConversationModal, setShowConversationModal] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [segmentId, setSegmentId] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [loading, setLoading] = useState({});
    const [suggestedResponse, setSuggestedResponse] = useState({});
    const [copyClicked, setCopyClicked] = useState(false)
    
    // Audio recording state
    const [isRecording, setIsRecording] = useState({});
    const [recordingTime, setRecordingTime] = useState({});
    const [audioBlobs, setAudioBlobs] = useState({});
    const [audioURLs, setAudioURLs] = useState({});
    const [isPlaying, setIsPlaying] = useState({});
    const [mediaRecorders, setMediaRecorders] = useState({});
    const [recordingStreams, setRecordingStreams] = useState({});
    const recordingTimeRefs = useRef({});
    const audioPlayerRefs = useRef({});
    /** Refs mirror state so unmount cleanup does not run when recorders/streams update (that was clearing the duration interval). */
    const mediaRecordersRef = useRef({});
    const recordingStreamsRef = useRef({});
    const audioURLsRef = useRef({});
    
    // Function to get current component state
    const getCurrentState = () => {
        return {
            currentModuleIndex,
            userAnswers,
            submittedAnswers,
            trainingScores,
            expandedExamples,
            conversationDetails,
            selectedSegmentIdx,
            suggestedResponse,
            audioLoading,
            audioBlobs,
            audioURLs
        };
    };
    
    // Function to restore component state
    const restoreState = (state) => {
        if (state) {
            setCurrentModuleIndex(state.currentModuleIndex || 0);
            setUserAnswers(state.userAnswers || {});
            setSubmittedAnswers(state.submittedAnswers || {});
            setTrainingScores(state.trainingScores || {});
            setExpandedExamples(state.expandedExamples || {});
            setConversationDetails(state.conversationDetails || {});
            setSelectedSegmentIdx(state.selectedSegmentIdx || {});
            setSuggestedResponse(state.suggestedResponse || {});
            setAudioLoading(state.audioLoading || {});
            setAudioBlobs(state.audioBlobs || {});
            setAudioURLs(state.audioURLs || {});
        }
    };
    // Get user credentials from cookie
    function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }

    useEffect(() => {
        if(emailID && clientID && serviceID){
            setEmail(emailID)
            setClient_id(clientID)
            setService_id(serviceID)
        }
    }, [emailID, clientID, serviceID]);
    
    // Restore state after refresh
    useEffect(() => {
        if (savedState && !isRefreshing) {
            restoreState(savedState);
            onStateRestore(); // Clear the saved state
        }
    }, [savedState, isRefreshing]);

    useEffect(() => {
        // Process modules from trainingData (now an array of objects)
        const processedModules = (trainingData?.modules || [])
            .sort((a, b) => (a.module_id || 0) - (b.module_id || 0))
            .map((mod, index) => ({
                id: mod.module_name || `module_${index}`,
                module_id: mod.module_id,
                ans: mod.ans,
                score: mod.score,
                negative_points: mod.negative_points || [],
                positive_points: mod.positive_points || [],
                total_questions: mod.total_questions,
                completion_status: mod.completion_status,
                completed_questions: mod.completed_questions,
                training_materials: training_material ? training_material[mod.module_name] || {} : {},
                // Transform training_scenarios to training_examples for compatibility
                training_examples: (mod.training_scenarios || [])
                    .sort((a, b) => (a.scenario_id || 0) - (b.scenario_id || 0))
                    .map((scenario, scenarioIndex) => ({
                        // CRITICAL FIX: Make IDs unique across modules by including module_id
                        id: `module_${mod.module_id}_scenario_${scenario.scenario_id || scenarioIndex}`,
                        scenario_id: scenario.scenario_id,
                        score: scenario.score || 0,
                        status: scenario.completion_status || 'pending',
                        question: scenario.question || [],
                        suggested_response: scenario.suggested_response,
                        agent_answer: scenario.agent_answer,
                        agent_audio_transcript: scenario.agent_audio_transcript,
                        agent_answer_feedback: scenario.agent_answer_feedback,
                        // Extract conversation_id from the first source in the first question
                        conversation_id: scenario.question?.[0]?.sources?.[0]?.conversation_id || null,
                        answer_type: scenario?.answer_type || null,
                        agent_answer_presigned_url: scenario?.agent_answer_presigned_url || null,
                        sequence: scenario.scenario_id || scenarioIndex + 1
                    }))
            }));
        setModules(processedModules);

        // Initialize states with unique IDs
        const initialAnswers = {};
        const initialScores = {};
        const initialExpanded = {};
        const initialAudioLoading = {};
        processedModules.forEach(mod => {
            mod.training_examples.forEach(ex => {
                initialAnswers[ex.id] = '';
                initialScores[ex.id] = { score: ex.score || 0, status: ex.status || 'pending' };
                initialExpanded[ex.id] = false; // Default to collapsed
                initialAudioLoading[ex.id] = false;
            });
        });
        
        // Only set initial state if we're not restoring from saved state
        if (!savedState) {
            setUserAnswers(initialAnswers);
            setTrainingScores(initialScores);
            setExpandedExamples(initialExpanded);
            setAudioLoading(initialAudioLoading);
        }

        // Fetch conversation details for initially expanded examples
        // Only fetch if we don't have saved state (not during refresh)
        if (email && client_id && service_id && !savedState) {
            processedModules.forEach(mod => {
                mod.training_examples.forEach(ex => {
                    if (ex.conversation_id) {
                        fetchConversationDetails(ex.conversation_id, ex.id);
                    }
                });
            });
        }
    }, [trainingData, email, client_id, service_id]);

    // Effect to handle module changes and fetch conversation details
    useEffect(() => {
        if (modules.length > 0 && currentModuleIndex < modules.length && !isRefreshing) {
            const currentModule = modules[currentModuleIndex];
            
            // Fetch conversation details for current module's scenarios
            if (currentModule && email && client_id && service_id) {
                currentModule.training_examples.forEach(ex => {
                    if (ex.conversation_id && !conversationDetails[ex.id]) {
                        fetchConversationDetails(ex.conversation_id, ex.id);
                    }
                });
            }
        }
    }, [currentModuleIndex, modules, email, client_id, service_id, isRefreshing]);

    // Function to fetch conversation details
    const fetchConversationDetails = async (conversationId, exampleId) => {
        if (!conversationId || !email || !client_id || !service_id) return;
        
        try {
            setAudioLoading(prev => ({ ...prev, [exampleId]: true }));
            
            // Fetch conversation details
            let res = await server.getConversationDetails(conversationId, client_id, service_id, 'agent-feedback', 'training', email);
            if (res.status === 200) {
                let resp = await res.json();
                setConversationDetails(prev => ({
                    ...prev,
                    [exampleId]: resp.data
                }));
            }
        } catch (error) {
        } finally {
            setAudioLoading(prev => ({ ...prev, [exampleId]: false }));
        }
    };

    // Function to format time
    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    // Function to handle audio time update
    const handleAudioTimeUpdate = (segments, exampleId) => {
        if (!audioRefs.current[exampleId]) return;
        
        const currentTime = audioRefs.current[exampleId].currentTime;

        // Find the segment corresponding to the current audio time
        segments.data.forEach((segment, idx) => {
            if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
                if (segmentRefs.current[exampleId] && segmentRefs.current[exampleId][idx]) {
                    // Scroll the corresponding segment into view if it's not already visible
                    segmentRefs.current[exampleId][idx].scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });

                    // Highlight the selected segment
                    setSelectedSegmentIdx(prev => ({
                        ...prev,
                        [exampleId]: idx
                    }));
                }
            }
        });
    };

    // Function to toggle segment
    const toggleSegment = (item, idx, exampleId) => {
        setSelectedSegmentIdx(prev => ({
            ...prev,
            [exampleId]: idx
        }));
        
        if (audioRefs.current[exampleId]) {
            audioRefs.current[exampleId].currentTime = item.start_time || item.timestamp;
            audioRefs.current[exampleId].play();
        }
    };

    // Function to get border color for segments
    const getBorderColor = (actor_type) => {
        switch (actor_type) {
            case 'user':
                return "4px solid #6f6ffe";
            case 'agent':
                return "4px solid #3B82F6";
            case 'bot':
                return "4px solid gray";
            case 'system':
                return "4px solid gray";
            default:
                return "4px solid transparent";
        }
    };

    // Audio recording functions
    const startRecording = async (exampleId) => {
        try {
            // Check browser compatibility
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                showToast('Audio recording is not supported in this browser. Please use a modern browser.', 'error');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Try to use audio/webm, fallback to default if not supported
            let mimeType = 'audio/webm';
            if (!MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = '';
            }
            
            const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
            const chunks = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' });
                const audioURL = URL.createObjectURL(blob);
                
                setAudioBlobs(prev => ({ ...prev, [exampleId]: blob }));
                setAudioURLs(prev => ({ ...prev, [exampleId]: audioURL }));
                setIsRecording(prev => ({ ...prev, [exampleId]: false }));
                setRecordingTime(prev => ({ ...prev, [exampleId]: 0 }));
                
                // Stop all tracks in the stream
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(prev => ({ ...prev, [exampleId]: true }));
            setRecordingTime(prev => ({ ...prev, [exampleId]: 0 }));
            setMediaRecorders(prev => ({ ...prev, [exampleId]: mediaRecorder }));
            setRecordingStreams(prev => ({ ...prev, [exampleId]: stream }));

            // Start timer
            const startTime = Date.now();
            recordingTimeRefs.current[exampleId] = setInterval(() => {
                const currentTime = Math.floor((Date.now() - startTime) / 1000);
                setRecordingTime(prev => ({
                    ...prev,
                    [exampleId]: currentTime
                }));
                
                // Auto-stop recording after 2 minutes (120 seconds)
                if (currentTime >= 120) {
                    stopRecording(exampleId);
                    showToast('Recording stopped automatically after 2 minutes.', 'success');
                }
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            if (error.name === 'NotAllowedError') {
                showToast('Microphone access denied. Please allow microphone permissions and try again.', 'error');
            } else if (error.name === 'NotFoundError') {
                showToast('No microphone found. Please connect a microphone and try again.', 'error');
            } else {
                showToast('Error accessing microphone. Please check permissions.', 'error');
            }
        }
    };
    function formatEpochTime(epochMillis) {
        if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
          throw new Error('Input must be a 13-digit epoch timestamp in milliseconds.');
        }
      
        const date = new Date(epochMillis);
        return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
    }
    const getBackgroundColor = (actor_type) => {
        switch (actor_type) {
          case 'user':
            return"rgb(249, 250, 251)"
          case 'agent':
            return"rgb(249, 250, 251)"
          case 'bot':
            return"rgb(249, 250, 251)"
          case 'system':
            return"rgb(249, 250, 251)"
          default:
            break;
        }
    }

    const stopRecording = (exampleId) => {
        const mediaRecorder = mediaRecorders[exampleId];
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        
        // Clear timer
        if (recordingTimeRefs.current[exampleId]) {
            clearInterval(recordingTimeRefs.current[exampleId]);
            recordingTimeRefs.current[exampleId] = null;
        }
    };

    const startNewRecording = async (exampleId) => {
        // If there's an existing recording, delete it first
        if (audioBlobs[exampleId]) {
            deleteRecording(exampleId);
        }
        
        // Start new recording
        await startRecording(exampleId);
    };

    const playRecording = (exampleId) => {
        const audioPlayer = audioPlayerRefs.current[exampleId];
        if (audioPlayer) {
            if (isPlaying[exampleId]) {
                audioPlayer.pause();
                setIsPlaying(prev => ({ ...prev, [exampleId]: false }));
            } else {
                audioPlayer.play();
                setIsPlaying(prev => ({ ...prev, [exampleId]: true }));
            }
        }
    };

    const deleteRecording = (exampleId) => {
        // Revoke object URL to free memory
        if (audioURLs[exampleId]) {
            URL.revokeObjectURL(audioURLs[exampleId]);
        }
        
        setAudioBlobs(prev => {
            const newBlobs = { ...prev };
            delete newBlobs[exampleId];
            return newBlobs;
        });
        
        setAudioURLs(prev => {
            const newURLs = { ...prev };
            delete newURLs[exampleId];
            return newURLs;
        });
        
        setIsPlaying(prev => ({ ...prev, [exampleId]: false }));
    };

    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswerChange = (exampleId, answer) => {
        setUserAnswers(prev => ({ ...prev, [exampleId]: answer }));
    };

    const handleReattempt = (exampleId) => {
        setTrainingScores(prev => ({
            ...prev,
            [exampleId]: { score: 0, status: 'pending' }
        }));
        setSubmittedAnswers(prev => {
            const next = { ...prev };
            delete next[exampleId];
            return next;
        });
        setUserAnswers(prev => ({
            ...prev,
            [exampleId]: ''
        }));
        setSuggestedResponse(prev => {
            const next = { ...prev };
            delete next[exampleId];
            return next;
        });
        deleteRecording(exampleId);
        setModules(prev => prev.map(mod => ({
            ...mod,
            training_examples: mod.training_examples.map(ex =>
                ex.id === exampleId
                    ? {
                        ...ex,
                        score: 0,
                        status: 'pending',
                        agent_answer: null,
                        agent_audio_transcript: null,
                        agent_answer_feedback: null,
                        agent_answer_presigned_url: null
                    }
                    : ex
            )
        })));
    };

    const handleSubmit = async (exampleId) => {
        setLoading(prev => ({ ...prev, [exampleId]: true }));
        
        const textAnswer = userAnswers[exampleId];
        const audioBlob = audioBlobs[exampleId];
        const example = modules[currentModuleIndex].training_examples.find(ex => ex.id === exampleId);
        const recordType = conversationDetails[exampleId]?.record_type;
        let suggestedResponse = ''
        
        if (recordType === 'chat' && (!textAnswer || textAnswer.trim() === '')) {
            showToast('Please provide a text answer', 'error');
            setLoading(prev => ({ ...prev, [exampleId]: false }));
            return;
        }

        if (recordType === 'call' && !audioBlob) {
            showToast('Please record an audio answer', 'error');
            setLoading(prev => ({ ...prev, [exampleId]: false }));
            return;
        }

        let score = 0;
        
        // Create FormData for sending
        const formData = new FormData();
        formData.append('client_id', client_id);
        formData.append('service_id', service_id);
        formData.append('email', email);
        formData.append('menu', 'agent-feedback');
        formData.append('submenu', 'training');
        formData.append('training_session_id', training_session_id);
        formData.append('module_id', currentModule.module_id);
        formData.append('module_name', currentModule.id);
        formData.append('scenario_id', example.scenario_id);
        formData.append('agent_answer', recordType === 'chat' ? textAnswer : '');
        
        // Calls are answered with audio; chats are answered with text.
        if (recordType === 'call' && audioBlob) {
            try {
                const fileExtension = audioBlob.type === 'audio/webm' ? 'webm' : 'mp3';
                formData.append('audio_answer', audioBlob, `recording.${fileExtension}`);
            } catch (error) {
                console.error('Error processing audio file:', error);
                showToast('Error processing audio file', 'error');
                setLoading(prev => ({ ...prev, [exampleId]: false }));
                return;
            }
        }
        
        // Send FormData instead of JSON
        let res = await server.evaluateAgentResponse(formData);
        
        if(res.status === 200){
            let resp = await res.json()
            score = resp.data.score
            suggestedResponse = resp.data.suggested_response
            
            // Don't update local state immediately to prevent green flash
            // State will be updated after refresh through state restoration
            
            // Small delay to prevent visual flash before triggering refresh
            setTimeout(() => {
                // Save current state and trigger refresh
                const currentState = getCurrentState();
                // Update the state with the new values
                currentState.trainingScores[exampleId] = { score, status: 'completed' };
                currentState.submittedAnswers[exampleId] = textAnswer || 'Audio Answer';
                currentState.suggestedResponse[exampleId] = suggestedResponse;
                // Ensure the current question remains expanded
                currentState.expandedExamples[exampleId] = true;
                
                prop_back_api_call(true, currentState);
            }, 100); // Small delay to let UI settle
        }else{
            let resp = await res.json()
            showToast(resp.error, 'error');
            setLoading(prev => ({ ...prev, [exampleId]: false }));
            return;
        }
        setLoading(prev => ({ ...prev, [exampleId]: false }));
    };

    const toggleExample = (exampleId) => {
        setExpandedExamples(prev => ({ ...prev, [exampleId]: !prev[exampleId] }));
        
        // Fetch conversation details when expanding
        if (!expandedExamples[exampleId]) {
            const example = modules[currentModuleIndex]?.training_examples.find(ex => ex.id === exampleId);
            if (example?.conversation_id && !conversationDetails[exampleId]) {
                fetchConversationDetails(example.conversation_id, exampleId);
            }
        }
    };

    const handleNext = () => {
        if (currentModuleIndex < modules.length - 1) {
            const nextIndex = currentModuleIndex + 1;
            setCurrentModuleIndex(nextIndex);
        }
    };

    const handlePrev = () => {
        if (currentModuleIndex > 0) {
            const prevIndex = currentModuleIndex - 1;
            setCurrentModuleIndex(prevIndex);
        }
    };

    const handleClose = () => {
        setShowConversationModal(false);
        setConversationId(null);
        setSegmentId(null);
        setSelectedModule(null);
    };

    const currentModule = modules[currentModuleIndex];



    const isModuleCompleted = (module) => {
        return module && module.training_examples && module.training_examples.length > 0 
            ? module.training_examples.every(ex => trainingScores[ex.id]?.status === 'completed')
            : false;
    };

    // TEMPORARY: Test function to verify unique IDs (remove after testing)
    const testUniqueIds = () => {
        const allIds = [];
        modules.forEach(mod => {
            mod.training_examples.forEach(ex => {
                allIds.push(ex.id);
            });
        });
        const uniqueIds = [...new Set(allIds)];
        return allIds.length === uniqueIds.length;
    };

    // Run test when modules change (remove after testing)
    useEffect(() => {
        if (modules.length > 0) {
            const isUnique = testUniqueIds();
            if (!isUnique) {
            } else {
            }
        }
    }, [modules]);

    mediaRecordersRef.current = mediaRecorders;
    recordingStreamsRef.current = recordingStreams;
    audioURLsRef.current = audioURLs;

    // Cleanup audio recording on component unmount only (deps on recorders/streams cleared timers on every start)
    useEffect(() => {
        return () => {
            Object.keys(mediaRecordersRef.current).forEach((exampleId) => {
                const mediaRecorder = mediaRecordersRef.current[exampleId];
                if (mediaRecorder && mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }
            });

            Object.keys(recordingStreamsRef.current).forEach((exampleId) => {
                const stream = recordingStreamsRef.current[exampleId];
                if (stream) {
                    stream.getTracks().forEach((track) => track.stop());
                }
            });

            Object.keys(recordingTimeRefs.current).forEach((exampleId) => {
                if (recordingTimeRefs.current[exampleId]) {
                    clearInterval(recordingTimeRefs.current[exampleId]);
                }
            });

            Object.keys(audioURLsRef.current).forEach((exampleId) => {
                if (audioURLsRef.current[exampleId]) {
                    URL.revokeObjectURL(audioURLsRef.current[exampleId]);
                }
            });
        };
    }, []);
    const renderExplanations = (data, module, type=null, exampleId=null) => {
        let source_id_map = {}
        let currentIndex = 1;

        for (let i = 0; i < data.length; i++) {
            for (let j = 0; j < data[i].sources.length; j++) {
              if(data[i].sources[j]?.conversation_id && data[i].sources[j]?.segment_id){
                let id = data[i].sources[j]?.conversation_id +'_'+ data[i].sources[j]?.segment_id;
                if (!source_id_map[id]) {
                  source_id_map[id] = currentIndex++;
                }
              }else{
                source_id_map[data[i].sources[j]?.segment_id] = currentIndex++;
              }
            }
        }
        const formattedTime = (time) => {
            if(!time || time === ''){
                return ''
            }
            return new Date(time * 1000).toLocaleString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            });
          };
          
          const handleSourceClick = (source) => {
            setShowConversationModal(true);
            setConversationId(source.conversation_id);
            setSegmentId(source);
            setSelectedModule(module);
          }
          
          const handleSegmentJump = (source, exampleId) => {
            const conversationData = conversationDetails[exampleId];
            if (!conversationData) return;

            // Chat conversations: scroll to message by message_id using data attribute
            if (conversationData.record_type === 'chat') {
              const messageIndex = Array.isArray(conversationData.messages)
                ? conversationData.messages.findIndex(message => message.message_id === source.segment_id)
                : -1;

              if (messageIndex !== -1) {
                // Update selected index state (not currently used for chat styling but harmless)
                setSelectedSegmentIdx(prev => ({ ...prev, [exampleId]: messageIndex }));

                // Scroll and highlight the chat message element
                setTimeout(() => {
                  const el = document.querySelector(`[data-message-id="${source.segment_id}"]`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    const prevBg = el.style.backgroundColor;
                    el.style.backgroundColor = 'rgba(111, 111, 254, 0.1)';
                    setTimeout(() => { el.style.backgroundColor = prevBg || ''; }, 2000);
                  }
                }, 100);
              }
              return;
            }

            // Call recordings: find segment, seek audio, scroll using refs
            const segmentIndex = (conversationData.segments && Array.isArray(conversationData.segments.data))
              ? conversationData.segments.data.findIndex(segment => segment.segment_id === source.segment_id)
              : -1;

            if (segmentIndex !== -1) {
              const targetSegment = conversationData.segments.data[segmentIndex];

              if (audioRefs.current[exampleId]) {
                audioRefs.current[exampleId].currentTime = targetSegment.start_time;
                audioRefs.current[exampleId].play();
              }

              setSelectedSegmentIdx(prev => ({ ...prev, [exampleId]: segmentIndex }));

              if (segmentRefs.current[exampleId] && segmentRefs.current[exampleId][segmentIndex]) {
                segmentRefs.current[exampleId][segmentIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }
          }
          return (
            <>
                          {
                type && type === 'bulletpoints' ? <ul style={{listStyleType: "disc", paddingLeft: "20px", fontSize: "13px"}}>
                {data && data.map((item, idx) => {
                  let title = item.title || null;
                  return (
                    <span key={idx}>
                    {title && <div className='font-semibold' style={{fontSize: "14px"}}>{title}</div>}
                    <li key={idx} style={{fontSize: "13px", marginBottom: "10px"}}>{item.text}
                    <span style={{wordBreak: "break-all"}}>{item.sources.map((source, idx) => (
                  <span key={idx} onClick={() => exampleId ? handleSegmentJump(source, exampleId) : handleSourceClick(source)} className="bg-gray-100 p-1 mx-1 rounded text-gray-600 text-xs cursor-pointer">{source_id_map[source.conversation_id +'_'+ source.segment_id] || formattedTime(source.start_timestamp) || source_id_map[source.segment_id]}</span>
                ))}</span>
                    </li>
                    </span>
                  )
                })}
              </ul>
              :
              <>
              {data && data.map((item, idx) => {
                let title = item.title || null;
              return (
                <span key={idx} style={{marginBottom: "10px"}}>
                    {title && <div className='font-semibold' style={{fontSize: "14px"}}>{title}</div>}
                  <span style={{fontSize: "13px"}}>{item.text}
                  <span style={{wordBreak: "break-all"}}>{item.sources.map((source, idx) => (
                  <span key={idx} onClick={() => exampleId ? handleSegmentJump(source, exampleId) : handleSourceClick(source)} className="bg-gray-100 p-1 mx-1 rounded text-gray-600 text-xs cursor-pointer">{source_id_map[source.conversation_id +'_'+ source.segment_id] || formattedTime(source.start_timestamp) || source_id_map[source.segment_id]}</span>
                ))}</span>
                </span>
              </span>
            )
          })}</>
            }
          </>
          )
      }

    return (
        <div className="bg-white h-full overflow-y-auto border rounded-lg mb-5">
            <div className='pt-4 mb-4' style={{borderRadius: "0.5rem 0.5rem 0 0"}}>
            <h1 className="text-lg font-semibold mb-4 px-4">Progress</h1>
            <hr />
            </div>
            <div className='p-4'>
                {/* Progress Bar */}
            <div className="mb-4">
                <div className="flex justify-center">
                    <div className="w-full max-w-5xl overflow-x-auto">
                        <div className="flex items-baseline justify-center px-4 py-6" style={{minWidth: "800px", marginLeft: (modules.length > 6) ? 6*(Math.abs(modules.length - 6))+"rem" : 0}}>
                            {modules.map((module, index) => (
                                <div key={module.id} className="flex items-baseline">
                                    <div className="flex flex-col items-center relative" style={{minWidth: "80px"}}>
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                                                index === currentModuleIndex ? 'bg-indigo-600 text-white' : 
                                                (isModuleCompleted(module) ? 'bg-indigo-600 text-white' : 'bg-white text-black border-2 border-gray-400')
                                            }`}
                                        >
                                            {isModuleCompleted(module) ? '✔' : index + 1}
                                        </div>
                                        <div className="mt-3 text-center">
                                            <p className={`text-sm leading-tight max-w-[100px] capitalize ${index <= currentModuleIndex ? 'font-semibold text-indigo-600' : 'font-normal text-black'}`}>
                                                {module.id.replace(/_/g, ' ')}
                                            </p>
                                        </div>
                                    </div>
                                    {index < modules.length - 1 && (
                                        <div className="flex-1 min-w-[80px]">
                                            <div className="border-t-2 border-gray-300 w-full"></div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Module Content */}
            <div className="bg-white rounded-lg">
                <div className='bg-gray-100 p-4 mb-4' style={{borderRadius: "0.5rem 0.5rem 0 0"}}>
                <h2 className="text-lg font-semibold px-4 capitalize">{currentModule?.id?.replace(/_/g, ' ')}</h2>
                </div>
                {currentModule?.training_materials && currentModule?.training_materials?.materials && currentModule?.training_materials?.materials?.length > 0 && <div className="flex items-center space-x-2 mb-4">
                    <h2 className="text-md font-semibold px-4 capitalize">Training Materials</h2>
                    {currentModule?.training_materials?.materials?.map((material, idx) => (
                        <div key={idx} className="text-sm px-2 py-1 rounded-md bg-gray-200 bg-indigo-50 text-indigo-600" style={{fontSize: "14px", padding: "5px 15px"}}>
                            <a href={material.link} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 gap-2">
                            {material.name} <FaExternalLinkAlt size={16} />
                            </a>
                        </div>
                    ))}
                </div>}
                <div className='px-4'>
                    {
                        currentModule?.explanation_with_source && <div className="flex gap-3 mb-6">
                            <div className="border rounded-lg w-full">
                                <div style={{overflow: "auto", height: "250px", padding: "10px"}}>
                                    {currentModule?.explanation_with_source && renderExplanations(currentModule?.explanation_with_source, 'Agent Feedback')}
                                </div>
                            </div>
                        </div>
                    }
                {currentModule?.positive_points && currentModule?.negative_points && <div className="flex gap-3 mb-6">
                                <div className="border rounded-lg w-1/2">
                                    <div className="text-md p-3 bg-green-50 text-green-900 rounded-t-lg">Key Positive Points</div>
                                    <div style={{overflow: "auto", height: "250px", padding: "10px"}}>
                                        {currentModule?.positive_points && renderExplanations(currentModule?.positive_points, 'Agent Feedback', 'bulletpoints')}
                                    </div>
                                </div>
                                
                                <div className="border rounded-lg w-1/2">
                                    <div className="text-md p-3 bg-orange-50 text-orange-900 rounded-t-lg">Key Improvements</div>
                                    <div style={{overflow: "auto", height: "250px", padding: "10px"}}>
                                        {currentModule?.negative_points && renderExplanations(currentModule?.negative_points, 'Agent Feedback', 'bulletpoints')}
                                    </div>
                                </div>
                            </div>}

                {/* Training Examples */}
                <div className="space-y-4">
                    {currentModule?.training_examples && currentModule?.training_examples?.length > 0 ? (
                        currentModule?.training_examples?.map(example => {
                            const scoreInfo = trainingScores[example.id] || { score: 0, status: 'pending' };
                            const isCompleted = scoreInfo.status === 'completed';
                            const conversationData = conversationDetails[example.id];
                            const isLoadingAudio = audioLoading[example.id];
                            const recordType = conversationData?.record_type;

                            return (
                                <div key={example.id} className="border rounded-lg">
                                    <div className="flex justify-between items-center cursor-pointer px-2" style={{height: "70px"}} onClick={() => toggleExample(example.id)}>
                                        <div className="flex items-center space-x-2">
                                            {expandedExamples[example.id] ? <MdExpandMore size={16} /> : <ChevronRight size={16} />}
                                            {
                                                scoreInfo.score === 1 && isCompleted ? 
                                                <div className="py-1 rounded-md" style={{fontSize: "14px"}}><FaCheckCircle style={{borderRadius: "50%"}} className='bg-indigo-50 text-indigo-600' size={18} /></div> : 
                                               scoreInfo.score === 0 && isCompleted ? <div className="py-1 rounded-md" style={{fontSize: "14px"}}><IoClose style={{ borderRadius: "50%"}} className='bg-red-600 text-red-50' size={18} /></div> : 
                                               <div className="py-1 rounded-md" style={{fontSize: "14px"}}><CircleCheck style={{color: "white", background: "white", border: "1px solid #6f6ffe", borderRadius: "50%"}} size={18} /></div>
                                            }
                                            <h3 className="text-md font-medium ml-0">Training Scenario {example.sequence}</h3>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <span className="text-sm px-2 py-1 rounded-md bg-gray-200 bg-indigo-50 text-indigo-600" style={{fontSize: "14px", padding: "5px 15px"}}>
                                                {isCompleted && scoreInfo.score === 1 ? `${scoreInfo.score}/1` : isCompleted && scoreInfo.score === 0 ? `${scoreInfo.score}/1` : 'NA'}
                                            </span>
                                        </div>
                                    </div>
                                    {expandedExamples[example.id] && <hr className='mb-3'/>}

                                    {expandedExamples[example.id] && (
                                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-8 pt-0">
                                            <div>
                                                {/* Display all question parts */}
                                                <div className="mb-4 space-y-3">
                                                    {example?.question && renderExplanations(example?.question, 'Agent Feedback', 'bulletpoints', example.id)}
                                                </div>
                                                
                                                {isCompleted && !loading[example?.id] && !isRefreshing ? (
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="font-semibold text-sm">Your Answer</label>
                                                            <div className="p-2 border rounded-lg bg-gray-50 text-sm mt-1">
                                                                {submittedAnswers[example?.id] === 'Audio Answer' ? (
                                                                    <div className="flex items-center space-x-2">
                                                                        <audio src={example?.agent_answer_presigned_url} controls />
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center space-x-2">
                                                                        <span>{
                                                                            example?.answer_type === 'audio' ? <audio src={example?.agent_answer_presigned_url} controls /> : submittedAnswers[example?.id] || example?.agent_answer}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {(example?.answer_type === 'audio' || submittedAnswers[example?.id] === 'Audio Answer') && example?.agent_audio_transcript?.trim() && (
                                                            <div>
                                                                <div className="flex items-center gap-1">
                                                                    <label className="font-semibold text-sm">Transcribed answer:</label>
                                                                    <button
                                                                        type="button"
                                                                        className="text-gray-500 hover:text-gray-700"
                                                                        title="If your audio is transcribed incorrectly, please reattempt."
                                                                        aria-label="If your audio is transcribed incorrectly, please reattempt."
                                                                    >
                                                                        <Info size={14} />
                                                                    </button>
                                                                </div>
                                                                <div className="p-2 border rounded-lg bg-gray-50 text-sm mt-1 whitespace-pre-wrap break-words">
                                                                    {example.agent_audio_transcript}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <label className="font-semibold text-sm">Answer Feedback</label>
                                                            <div className={`p-2 border rounded-lg ${example?.agent_answer_feedback?.ans === 'Poor' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'} text-sm mt-1`} style={{
                                                                opacity: isRefreshing ? 0.5 : 1,
                                                                transition: 'opacity 0.2s ease-in-out'
                                                            }}>
                                                                <div>{example?.agent_answer_feedback?.ans === 'Poor' ? 'This answer can be improved. Here is how:' : 'Great Job! You have successfully handled the scenario.'}</div>
                                                               {example?.agent_answer_feedback?.ans === 'Poor' && <br/>}
                                                                {example?.agent_answer_feedback?.ans === 'Poor' && example?.agent_answer_feedback?.explanation}
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <button
                                                                type="button"
                                                                className="px-4 py-2 rounded-md border border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                                                                onClick={() => handleReattempt(example?.id)}
                                                            >
                                                                Reattempt
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : loading[example?.id] ? (
                                                    <div className="flex justify-center items-center" style={{flexDirection: "column" }}>
                                                        <div className="loader"></div>
                                                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {recordType === 'chat' && (
                                                        <div className="mb-4">
                                                            <label className="font-semibold text-sm mb-2 block">Your Answer</label>
                                                            <textarea
                                                                className="w-full p-2 border rounded-lg"
                                                                rows="3"
                                                                placeholder="Type your answer here"
                                                                value={userAnswers[example?.id]}
                                                                onChange={(e) => handleAnswerChange(example?.id, e.target.value)}
                                                            ></textarea>
                                                        </div>
                                                        )}

                                                        {/* Audio Recording Section */}
                                                        {recordType === 'call' && <div className="mb-4">
                                                            <label className="font-semibold text-sm mb-2 block">Audio Answer</label>
                                                            <p className="text-xs text-gray-600 mb-2">
                                                                Maximum recording time: 2 minutes
                                                            </p>
                                                            
                                                            {/* Recording Controls */}
                                                            {!audioBlobs[example?.id] ? (
                                                                <div className="flex items-center space-x-2">
                                                                    {!isRecording[example?.id] ? (
                                                                        <button
                                                                            onClick={() => startRecording(example?.id)}
                                                                            className="flex items-center space-x-2 px-2 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-900 text-sm"
                                                                        >
                                                                            <Mic size={16} />
                                                                            <span>Start Recording</span>
                                                                        </button>
                                                                    ) : (
                                                                        <div className="flex items-center space-x-2">
                                                                            <div className="flex items-center space-x-2 px-2 py-1 bg-red-600 text-white rounded-md animate-pulse text-sm">
                                                                                <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                                                                <span>Recording...</span>
                                                                            </div>
                                                                            <button
                                                                                onClick={() => stopRecording(example?.id)}
                                                                                className="flex items-center space-x-2 px-2 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-900 text-sm"
                                                                            >
                                                                                <Square size={16} />
                                                                                <span>Stop Recording</span>
                                                                            </button>
                                                                            <span className="text-sm text-gray-600">
                                                                                {formatRecordingTime(recordingTime[example?.id] || 0)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    {/* Audio Player */}
                                                                    <audio
                                                                        ref={el => audioPlayerRefs.current[example?.id] = el}
                                                                        src={audioURLs[example?.id]}
                                                                        onEnded={() => setIsPlaying(prev => ({ ...prev, [example?.id]: false }))}
                                                                        onPause={() => setIsPlaying(prev => ({ ...prev, [example?.id]: false }))}
                                                                        onPlay={() => setIsPlaying(prev => ({ ...prev, [example?.id]: true }))}
                                                                        className="w-full"
                                                                    />
                                                                    
                                                                    {/* Audio Controls */}
                                                                    <div className="flex items-center space-x-2">
                                                                        <button
                                                                            onClick={() => playRecording(example?.id)}
                                                                            className="flex items-center space-x-2 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                                                        >
                                                                            {isPlaying[example?.id] ? <Pause size={14} /> : <Play size={14} />}
                                                                            <span>{isPlaying[example?.id] ? 'Pause' : 'Play'}</span>
                                                                        </button>
                                                                        
                                                                        <button
                                                                            onClick={() => deleteRecording(example?.id)}
                                                                            className="flex items-center space-x-2 px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                                                        >
                                                                            <XCircle size={14} />
                                                                            <span>Delete</span>
                                                                        </button>
                                                                        
                                                                        <button
                                                                            onClick={() => startNewRecording(example?.id)}
                                                                            className="flex items-center space-x-2 px-2 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-900 text-sm"
                                                                        >
                                                                            <Mic size={14} />
                                                                            <span>Record New</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>}

                                                        {/* Submit Summary */}
                                                        {/* {(userAnswers[example?.id] && userAnswers[example?.id].trim() !== '') || audioBlobs[example?.id] ? (
                                                            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                                                <p className="text-xs text-blue-800">
                                                                    <strong>Will submit:</strong> 
                                                                    {userAnswers[example?.id] && userAnswers[example?.id].trim() !== '' && ' Text answer'}
                                                                    {userAnswers[example?.id] && userAnswers[example?.id].trim() !== '' && audioBlobs[example?.id] && ' + '}
                                                                    {audioBlobs[example?.id] && ' Audio answer'}
                                                                </p>
                                                            </div>
                                                        ) : null} */}

                                                        {/* Submit Button */}
                                                        <div className="flex justify-end">
                                                            <button
                                                                type="button"
                                                                disabled={recordType === 'chat'
                                                                    ? !userAnswers[example?.id]?.trim()
                                                                    : recordType === 'call'
                                                                        ? !audioBlobs[example?.id]
                                                                        : true}
                                                                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
                                                                onClick={() => handleSubmit(example?.id)}
                                                            >
                                                                Submit Answer
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                            <div className=' word-break-all py-1 ml-4 flex items-center justify-end text-xs' style={{marginBottom: "10px"}}>Conversation ID: {example?.conversation_id.length > 20 ? example?.conversation_id.slice(0, 20) + '...' : example?.conversation_id} 
                                                {!copyClicked &&<Copy className='cursor-pointer ml-2 mr-2 text-xs h-4 w-4' onClick={() => {navigator.clipboard.writeText(example?.conversation_id); setCopyClicked(true); setTimeout(() => {setCopyClicked(false)}, 1000)}} />}
                                                {copyClicked &&<Check className='cursor-pointer ml-2 mr-2 text-xs h-4 w-4' />}
                                                </div>
                                            <div className="border rounded-lg">
                                                {isLoadingAudio ? (
                                                     <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                                                        <div className="loader"></div>
                                                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                                                    </div>
                                                ) : conversationData ? (
                                                    <div className="space-y-4">
                                                        {/* Audio Player */}
                                                        {conversationData.pre_signed_url && (
                                                            <div>
                                                                <div style={{ display: "flex", alignItems: "center", fontSize: "16px", fontWeight: "500", borderRadius: "10px 10px 0 0"}}>
                                                                </div>
                                                                <audio 
                                                                    controls 
                                                                    key={conversationData.pre_signed_url} 
                                                                    ref={el => audioRefs.current[example?.id] = el} 
                                                                    onTimeUpdate={() => handleAudioTimeUpdate(conversationData?.segments || [], example?.id)} 
                                                                    style={{ width: "95%", height: "40px", marginTop: "10px", marginLeft: "10px" }}
                                                                    src={conversationData.pre_signed_url}
                                                                />
                                                            </div>
                                                        )}
                                                        <hr/>
                                                        
                                                        {/* Segments */}
                                                        {conversationData.segments && conversationData.segments.data && conversationData.segments.data.length > 0 && (
                                                            <div>
                                                                <div className="max-h-64 overflow-y-auto" style={{padding: "10px"}}>
                                                                    {conversationData.segments.data.map((segment, idx) => (
                                                                        <div 
                                                                            key={idx}
                                                                            ref={el => {
                                                                                if (!segmentRefs.current[example.id]) segmentRefs.current[example.id] = {};
                                                                                segmentRefs.current[example.id][idx] = el;
                                                                            }}
                                                                            onClick={() => toggleSegment(segment, idx, example.id)}
                                                                            style={{marginBottom: "10px", borderLeft: selectedSegmentIdx[example.id] === idx ? "4px solid #6f6ffe":"", background: selectedSegmentIdx[example.id] === idx ? "rgba(249, 250, 251, 1)":"",
                                                                                padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}
                                                                        >
                                                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                                                                <div style={{fontSize: "14px", color: "grey"}}>{segment.speaker}</div>
                                                                                <div style={{fontSize: "14px", color: "grey"}}>{formatTime(segment.start_time)}</div>
                                                                            </div>
                                                                            <div style={{fontSize: "15px"}}>{segment.text}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {conversationData.record_type === 'chat' && <div className='max-h-72 overflow-y-auto' style={{padding: "10px"}}>
                                                        {
                                                            conversationData.record_type && conversationData.messages && Array.isArray(conversationData.messages) && conversationData.messages.length > 0 && conversationData.messages.map((item, idx)=> {
                                                                return (
                                                                <div key={`segment_${idx}`} ref={el => {
                                                                                if (!segmentRefs.current[example.id]) segmentRefs.current[example.id] = {};
                                                                                segmentRefs.current[example.id][idx] = el;
                                                                            }}
                                                                            onClick={() => toggleSegment(item, idx, example.id)}
                                                                data-message-id={item.message_id}
                                                                style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                                                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                                                    <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                                                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                                                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                                                    </div>
                                                                    <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                                                    </div>
                                                                    <div style={{fontSize: "14px"}}><span style={{wordBreak: "break-all"}}>{item.content?.text?.content} <br/></span></div>
                                                                    {item.content.image && item.content.image.url && 
                                                                    <div style={{fontSize: "15px", wordBreak: "break-all"}}>
                                                                        <br/>
                                                                    Image Shared - {item.content.image.url}
                                                                    </div>
                                                                    }
                                                                    {
                                                                    item.content.buttons && item.content.buttons.length > 0 && (
                                                                        <div>
                                                                        <br/>
                                                                        {item.actor_type === 'user' ? (
                                                                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                                                        ) : (
                                                                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                                                        )}
                                                                        </div>
                                                                    )
                                                                    }
                                                                </div>
                                                                )
                                                            })
                                                        }
                                                        </div>}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-center h-32">
                                                        <p className="text-gray-500">No audio data available</p>
                                                    </div>
                                                )}
                                            </div>
                                            </div>
                                            
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No training scenarios available for this module.</p>
                            <p className="text-sm text-gray-400 mt-2">
                                Module: {currentModule?.id} | Training Examples: {currentModule?.training_examples?.length || 0}
                            </p>
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-end mt-8 space-x-4">
                <button
                    onClick={handlePrev}
                    disabled={currentModuleIndex === 0}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                    <span> <ChevronLeft size={16} /> </span> Previous
                </button>
                <button
                    onClick={handleNext}
                    disabled={currentModuleIndex === modules.length - 1 || !isModuleCompleted(modules[currentModuleIndex])}
                    className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                    Next <span> <ChevronRight size={16} /> </span>
                </button>
            </div>
            </div>
            {
        showConversationModal && (
          <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "2%", top: "3.5%", width: "96%", height: "93%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                        zIndex: "999999999"}}>
                            <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={()=>{handleClose();}}><FaTimes style={{color: "slategray"}}/></div>
                            <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
          <CallDetails selectedOption={service_id} open_type={handleClose} callIdProp={conversationId} selectedClientId={client_id} selectedSegment={segmentId} selectedTab={selectedModule}/>
          </div>
          </div>
          </div>
        )
      }
        </div>
    );
};

export default AgentTraining;
